import React from "react";
import { useTranslation } from "react-i18next";
import {
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneLogs,
} from "@planzone/view-models";
import { events } from "@planzone/events";

import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { ProfileNavigation } from "@common/profileNavigation";
import { usePreferences } from "@components/preferences";
import { useMessage } from "@components/useMessage";
import { ToolBar } from "./toolBar";
import { Skeleton } from "./skeleton";
import { Logs } from "./logs";
import { Props, Filter } from "./types";
import styles from "./styles.scss";

function PlanzoneLogsPage(_: Props) {
  const { t } = useTranslation();
  const [filter, setFilter] = React.useState<Filter>({
    logType: [],
  });
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    retrievePlanzoneLogs,
    logs,
    numberPages,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
  } = useRetrievePlanzoneLogs(planzone.uuid);
  const [pageIndex, setPageIndex] = React.useState(0);
  const [hasUpdateFilters, setHasUpdateFilters] = React.useState(false);
  const pageSize = 20;

  useMessage((message) => {
    if (message.type === events.editPreferencesSucceeded) {
      setPageIndex(0);
      setHasUpdateFilters(true);
    }
    if (message.type === events.retrievePreferencesSucceeded) {
      retrievePlanzoneLogs({
        planzoneId: planzone.uuid,
        pageSize: pageSize,
        pageIndex: 0,
        logTypesFilter: filter.logType ? filter.logType : [],
      });
    }
  });

  React.useEffect(() => {
    retrievePlanzoneLogs({
      planzoneId: planzone.uuid,
      pageSize: pageSize,
      pageIndex: pageIndex,
      logTypesFilter: filter.logType ? filter.logType : [],
    });
    hasUpdateFilters && setHasUpdateFilters(false);
  }, [pageIndex, hasUpdateFilters]);

  return (
    <div className={styles.logs}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("planzoneLogsPage.title")}</h1>
        <div className={styles.profileNavigation}>
          <AssistantShortcut />
          <Notifications />
          <ProfileNavigation />
        </div>
      </div>
      <div className={styles.main}>
        <div className={styles.toolbar}>
          <ToolBar filter={filter} setFilter={setFilter} />
        </div>
        {(isRequestPending || (!isRequestSuccess && !isRequestFailure)) &&
        pageIndex == 0 ? (
          <Skeleton cardNumber={10} />
        ) : (
          <Logs
            logs={logs}
            isRequestPending={isRequestPending && pageIndex <= numberPages}
            canLoadNextPage={!isRequestPending && pageIndex <= numberPages}
            loadNextPage={() => {
              !isRequestPending && setPageIndex((value) => value + 1);
            }}
          />
        )}
      </div>
    </div>
  );
}

export { PlanzoneLogsPage };
